/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useEffect, useState } from 'react'

import { Alert, Button, DatePicker } from '@/components/ui'
import { useApi } from '@/hooks'
import logger from '@/utils/logger'

import { AddressAutocomplete, type GeocodedPlace } from './AddressAutocomplete'
import { AddressCoordinateWarning } from './AddressCoordinateWarning'
import { formatShipmentTiming, formatTimestamp } from './shipmentTiming'
import { TrackerCommitments } from './TrackerCommitments'
import type { GeoPoint, ShipmentRecord, ShipmentStatus, UpdateShipmentBody } from './types'
import { useTrackerShipments } from './useTrackerShipments'

const log = logger({ category: 'ShipmentUpdatePanel' })

/**
 * Moves offered from each status, mirroring the server. Anything final is
 * absent: the tracker list freezes a shipment's readings at its actual end, so
 * reopening one would rewrite already-reported history.
 */
const TRANSITIONS: Partial<
  Record<ShipmentStatus, { to: ShipmentStatus; label: string; requires?: 'actualStartAt' | 'actualEndAt' }[]>
> = {
  planned: [
    { to: 'active', label: 'Start shipment', requires: 'actualStartAt' },
    { to: 'cancelled', label: 'Cancel shipment' },
  ],
  // A shipment can be called off after it has left, not just before.
  active: [
    { to: 'completed', label: 'Complete shipment', requires: 'actualEndAt' },
    { to: 'cancelled', label: 'Cancel shipment' },
  ],
}

/** Statuses whose plan can still be corrected. */
export const isEditable = (status: ShipmentStatus | null | undefined): boolean =>
  status === 'planned' || status === 'active'

interface PlaceState {
  name: string
  address: string
  location: string
  latitude: number | null
  longitude: number | null
  /** Address typed over after a pick, so it may no longer match the point. */
  addressEdited: boolean
}

/**
 * Seed from what is stored, with the lookup field left blank.
 *
 * Putting the stored address back into the lookup made two boxes show the same
 * text, and editing that one implied it would re-geocode, which it does not.
 * Blank plus the existing coordinates reads correctly: the point is already
 * known, and the lookup is only there to replace it.
 */
const placeFrom = (name: string | null, address: string | null, location: GeoPoint | null): PlaceState => ({
  name: name ?? '',
  address: address ?? '',
  location: '',
  latitude: location?.latitude ?? null,
  longitude: location?.longitude ?? null,
  addressEdited: false,
})

/** True when a place now points somewhere other than where it was stored. */
const movedFrom = (place: PlaceState, stored: GeoPoint | null): boolean => {
  if (place.latitude === null || place.longitude === null) return false
  return place.latitude !== stored?.latitude || place.longitude !== stored.longitude
}

const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string; htmlFor?: string }> = ({
  label,
  children,
  hint,
  htmlFor,
}) => (
  <div className="form-control w-full">
    <label htmlFor={htmlFor} className="label-text mb-1 block">
      {label}
    </label>
    {children}
    {hint && <span className="label-text-alt mt-1 block text-base-content/60">{hint}</span>}
  </div>
)

/**
 * Which half of the editing UI to show. Split across two tabs because a planned
 * shipment offers both, and together they make one very long page.
 */
export type ShipmentUpdateMode = 'transition' | 'plan'

export interface ShipmentUpdatePanelProps {
  shipmentId: number
  companyId: number | null
  mode: ShipmentUpdateMode
  /** Called after a successful save so the caller can refresh its own data. */
  onUpdated?: (shipment: ShipmentRecord) => void
}

/**
 * Advance a shipment's status, or correct a planned one's schedule and route.
 *
 * Deliberately re-fetches rather than taking the map row as a prop: the row
 * carries no route detail, and editing against a summary risks writing back
 * stale values for fields it never held.
 */
export const ShipmentUpdatePanel: React.FC<ShipmentUpdatePanelProps> = ({ shipmentId, companyId, mode, onUpdated }) => {
  const { api } = useApi()
  const [shipment, setShipment] = useState<ShipmentRecord | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [saveError, setSaveError] = useState('')
  const [savedStatus, setSavedStatus] = useState<string>('')
  const [isSaving, setIsSaving] = useState(false)

  const [expectedStartAt, setExpectedStartAt] = useState<Date | null>(null)
  const [expectedEndAt, setExpectedEndAt] = useState<Date | null>(null)
  const [actualStartAt, setActualStartAt] = useState<Date | null>(null)
  const [actualEndAt, setActualEndAt] = useState<Date | null>(null)
  const [origin, setOrigin] = useState<PlaceState>(placeFrom(null, null, null))
  const [destination, setDestination] = useState<PlaceState>(placeFrom(null, null, null))

  const load = useCallback(async () => {
    setIsLoading(true)
    setLoadError('')
    try {
      const response = await api.get<ShipmentRecord>({
        path: `/shipments/${shipmentId}`,
        origin: 'ShipmentUpdatePanel#load',
      })
      if (response.error || !response.data) {
        setLoadError(response.message || 'Could not load this shipment')
        return
      }
      const found = response.data
      setShipment(found)
      setExpectedStartAt(found.expectedStartAt)
      setExpectedEndAt(found.expectedEndAt)
      setActualStartAt(found.actualStartAt)
      setActualEndAt(found.actualEndAt)
      setOrigin(placeFrom(found.originName, found.originAddress, found.originLocation))
      setDestination(placeFrom(found.destinationName, found.destinationAddress, found.destinationLocation))
    } catch (ex) {
      log.error(ex)
      setLoadError('Could not load this shipment')
    } finally {
      setIsLoading(false)
    }
  }, [api, shipmentId])

  useEffect(() => {
    void load()
  }, [load])

  const { shipments: trackerShipments, isLoading: trackerShipmentsLoading } = useTrackerShipments(
    companyId,
    shipment?.trackerId ?? null
  )

  const save = async (body: UpdateShipmentBody) => {
    setIsSaving(true)
    setSaveError('')
    setSavedStatus('')
    try {
      const response = await api.put<ShipmentRecord, UpdateShipmentBody>({
        path: `/shipments/${shipmentId}`,
        origin: 'ShipmentUpdatePanel#save',
        body,
      })
      if (response.error || !response.data) {
        setSaveError(response.message || 'Could not update the shipment')
        return
      }
      const updated = response.data
      setShipment(updated)
      setSavedStatus(updated.status)
      onUpdated?.(updated)
    } catch (ex) {
      log.error(ex)
      setSaveError('Could not update the shipment')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-base-content/70" data-testid="shipment-update-loading">
        <span className="loading loading-spinner loading-sm" />
        Loading shipment…
      </div>
    )
  }

  if (loadError || !shipment) {
    return <Alert type="error" message={loadError || 'Shipment not found'} />
  }

  const editable = isEditable(shipment.status)
  const transitions = TRANSITIONS[shipment.status] ?? []
  const canEditPlan = shipment.status === 'planned'

  const handleTransition = (to: ShipmentStatus, requires?: 'actualStartAt' | 'actualEndAt') => {
    if (requires === 'actualStartAt' && !actualStartAt) {
      setSaveError('Enter the actual start time before starting this shipment')
      return
    }
    if (requires === 'actualEndAt' && !actualEndAt) {
      setSaveError('Enter the actual end time before completing this shipment')
      return
    }
    // The server rejects this too, but it is the easiest mistake to make here —
    // the start is already fixed and off-screen, so say what it is rather than
    // making them go and look.
    if (actualEndAt && actualStartAt && actualEndAt <= actualStartAt) {
      setSaveError(`The actual end must be after the actual start (${formatTimestamp(actualStartAt)})`)
      return
    }
    void save({
      status: to,
      ...(requires === 'actualStartAt' ? { actualStartAt: actualStartAt?.toISOString() } : {}),
      ...(requires === 'actualEndAt' ? { actualEndAt: actualEndAt?.toISOString() } : {}),
    })
  }

  const handleSavePlan = () => {
    const body: UpdateShipmentBody = {}
    if (expectedStartAt && expectedStartAt.getTime() !== shipment.expectedStartAt?.getTime()) {
      body.expectedStartAt = expectedStartAt.toISOString()
    }
    if (expectedEndAt && expectedEndAt.getTime() !== shipment.expectedEndAt?.getTime()) {
      body.expectedEndAt = expectedEndAt.toISOString()
    }
    if (origin.name !== (shipment.originName ?? '')) body.originName = origin.name
    if (origin.address !== (shipment.originAddress ?? '')) body.originAddress = origin.address
    if (movedFrom(origin, shipment.originLocation)) {
      body.originLatitude = origin.latitude as number
      body.originLongitude = origin.longitude as number
    }
    if (destination.name !== (shipment.destinationName ?? '')) body.destinationName = destination.name
    if (destination.address !== (shipment.destinationAddress ?? '')) body.destinationAddress = destination.address
    if (movedFrom(destination, shipment.destinationLocation)) {
      body.destinationLatitude = destination.latitude as number
      body.destinationLongitude = destination.longitude as number
    }

    if (Object.keys(body).length === 0) {
      setSaveError('Nothing has changed')
      return
    }
    if (expectedStartAt && expectedEndAt && expectedEndAt <= expectedStartAt) {
      setSaveError('The expected end must be after the expected start')
      return
    }
    void save(body)
  }

  const place = (
    which: 'origin' | 'destination',
    state: PlaceState,
    setState: React.Dispatch<React.SetStateAction<PlaceState>>
  ) => (
    <div className="flex flex-col gap-3">
      <Field label={`${which === 'origin' ? 'Origin' : 'Destination'} name`} htmlFor={`update-${which}-name`}>
        <input
          id={`update-${which}-name`}
          className="input input-bordered w-full"
          value={state.name}
          onChange={(e) => setState((prev) => ({ ...prev, name: e.target.value }))}
          data-testid={`update-${which}-name`}
        />
      </Field>
      <AddressAutocomplete
        id={`update-${which}-location`}
        label="Location"
        value={state.location}
        coordinates={{ latitude: state.latitude, longitude: state.longitude }}
        onChange={(text) => setState((prev) => ({ ...prev, location: text, latitude: null, longitude: null }))}
        // The pick produced the coordinates, so its address replaces what was
        // there; hand-editing afterwards is what the warning below is for.
        onSelect={(picked: GeocodedPlace) =>
          setState((prev) => ({
            ...prev,
            location: picked.address,
            address: picked.address,
            latitude: picked.latitude,
            longitude: picked.longitude,
            addressEdited: false,
          }))
        }
        placeholder="Search for an address"
      />
      <Field label="Address" htmlFor={`update-${which}-address`} hint="Optional">
        <input
          id={`update-${which}-address`}
          className="input input-bordered w-full"
          value={state.address}
          onChange={(e) => setState((prev) => ({ ...prev, address: e.target.value, addressEdited: true }))}
          data-testid={`update-${which}-address`}
        />
        <AddressCoordinateWarning
          id={`update-${which}-address-warning`}
          show={state.addressEdited && state.latitude !== null}
        />
      </Field>
    </div>
  )

  return (
    <div className="flex flex-col gap-4" data-testid="shipment-update-panel">
      {savedStatus && <Alert type="success" message={`Shipment updated — now ${savedStatus}.`} dismissible />}
      {saveError && <Alert type="error" message={saveError} />}

      {/* Summary only on the transition tab. The plan tab edits these very
          fields a few rows down, so repeating them here just pushes the inputs
          off the top of the modal. */}
      {mode === 'transition' && (
        <section className="card bg-base-100 shadow">
          <div className="card-body gap-3">
            <h3 className="card-title text-base">
              {shipment.shipmentName}
              <span className="badge badge-sm badge-ghost">{shipment.status}</span>
            </h3>
            <p className="text-sm text-base-content/70">{formatShipmentTiming(shipment)}</p>
            <p className="text-sm text-base-content/70">
              {[shipment.originName, shipment.destinationName].filter(Boolean).join(' → ') || 'Route not set'}
            </p>

            {shipment.trackerId != null && (
              <TrackerCommitments
                trackerLabel={shipment.trackerLabel ?? `Tracker ${String(shipment.trackerId)}`}
                shipments={trackerShipments}
                isLoading={trackerShipmentsLoading}
                proposedStart={expectedStartAt}
                proposedEnd={expectedEndAt}
                ignoreShipmentId={shipment.id}
              />
            )}
          </div>
        </section>
      )}

      {/* Final statuses still show the shipment above, so there is something to
          read — only the controls go away. */}
      {!editable && (
        <Alert
          type="info"
          message={`This shipment is ${shipment.status}.`}
          description="Completed and cancelled shipments are final — no further changes are allowed."
        />
      )}

      {/* The plan tab is only offered while planned, so say why it is empty if a
          shipment starts underneath someone who already had it open. */}
      {mode === 'plan' && editable && !canEditPlan && (
        <Alert
          type="info"
          message="This shipment has already started."
          description="Once under way the schedule and route are what was agreed — use the Transition tab to complete or cancel it."
        />
      )}

      {mode === 'transition' && editable && (
        <section className="card bg-base-100 shadow">
          <div className="card-body gap-3">
            <h3 className="card-title text-base">Status</h3>
            <div className="grid gap-3 md:grid-cols-2">
              {/* Each transition records when it actually happened, so the time it
                needs sits next to the button that needs it. */}
              {shipment.status === 'planned' && (
                <Field label="Actual start" hint="Required to start the shipment" htmlFor="update-actual-start">
                  <DatePicker
                    inputId="update-actual-start"
                    shouldShowTime
                    initialDate={actualStartAt}
                    handleSet={setActualStartAt}
                  />
                </Field>
              )}
              {shipment.status === 'active' && (
                <Field label="Actual end" hint="Required to complete the shipment" htmlFor="update-actual-end">
                  <DatePicker
                    inputId="update-actual-end"
                    shouldShowTime
                    initialDate={actualEndAt}
                    handleSet={setActualEndAt}
                  />
                </Field>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {transitions.map(({ to, label, requires }) => (
                <Button
                  key={to}
                  handleClick={() => handleTransition(to, requires)}
                  variation={to === 'cancelled' ? 'secondary_gray_outline' : 'primary'}
                  isDisabled={isSaving}
                  size="sm"
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </section>
      )}

      {mode === 'plan' && canEditPlan && (
        <section className="card bg-base-100 shadow">
          <div className="card-body gap-3">
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Expected start" htmlFor="update-expected-start">
                <DatePicker
                  inputId="update-expected-start"
                  shouldShowTime
                  initialDate={expectedStartAt}
                  handleSet={setExpectedStartAt}
                />
              </Field>
              <Field label="Expected end" htmlFor="update-expected-end">
                <DatePicker
                  inputId="update-expected-end"
                  shouldShowTime
                  initialDate={expectedEndAt}
                  handleSet={setExpectedEndAt}
                />
              </Field>
            </div>

            {/* Kept on this tab even though the summary is gone: the clash
                warning is about the dates directly above it, and is not
                repeated anywhere else. */}
            {shipment.trackerId != null && (
              <TrackerCommitments
                trackerLabel={shipment.trackerLabel ?? `Tracker ${String(shipment.trackerId)}`}
                shipments={trackerShipments}
                isLoading={trackerShipmentsLoading}
                proposedStart={expectedStartAt}
                proposedEnd={expectedEndAt}
                ignoreShipmentId={shipment.id}
              />
            )}

            <div className="grid gap-4 lg:grid-cols-2">
              {place('origin', origin, setOrigin)}
              {place('destination', destination, setDestination)}
            </div>
            <div className="flex justify-end">
              <Button handleClick={handleSavePlan} variation="primary" isDisabled={isSaving} size="sm">
                {isSaving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
